import { useState, useEffect } from 'react';
import { parentChildApi } from '../../api/parent-child.api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  isActive: boolean;
}

interface Relationship {
  id: string;
  linkedAt: string;
  parent?: User;
  child?: User;
}

interface ParentChildManagerProps {
  userId: string;
  userRole: 'PARENT' | 'PLAYER';
  isEditable?: boolean;
}

export const ParentChildManager = ({ userId, userRole, isEditable = true }: ParentChildManagerProps) => {
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchRelationships();
  }, [userId, userRole]);

  const fetchRelationships = async () => {
    try {
      const response =
        userRole === 'PARENT'
          ? await parentChildApi.getParentChildren(userId)
          : await parentChildApi.getChildParents(userId);

      setRelationships(response.data.data || []);
    } catch (err) {
      console.error('Failed to load relationships:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchFirstName && !searchLastName) {
      setError('Please enter first name or last name to search');
      return;
    }

    setError('');
    setSearching(true);
    setSearchResults([]);
    setSelectedUser(null);

    try {
      const role = userRole === 'PARENT' ? 'PLAYER' : 'PARENT';
      const searchQuery = `${searchFirstName} ${searchLastName}`.trim();
      const response = await parentChildApi.searchUsers(role, searchQuery);
      const results = response.data.data || [];

      if (results.length === 0) {
        setError('No users found matching your search');
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Failed to search users:', err);
      setError('Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setSearchFirstName('');
    setSearchLastName('');
    setSearchResults([]);
    setSelectedUser(null);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setSearchFirstName('');
    setSearchLastName('');
    setSearchResults([]);
    setError('');
  };

  const handleLink = async () => {
    if (!selectedUser) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const data =
        userRole === 'PARENT'
          ? { parentId: userId, childId: selectedUser.id }
          : { parentId: selectedUser.id, childId: userId };

      await parentChildApi.linkParentChild(data);
      setSuccess(
        userRole === 'PARENT'
          ? 'Player linked successfully'
          : 'Parent linked successfully'
      );
      handleCloseDialog();
      fetchRelationships();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Link failed');
      } else {
        setError('Link failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (otherId: string) => {
    if (!window.confirm('Are you sure you want to remove this link?')) return;

    setError('');
    setSuccess('');

    try {
      const [parentId, childId] =
        userRole === 'PARENT' ? [userId, otherId] : [otherId, userId];

      await parentChildApi.unlinkParentChild(parentId, childId);
      setSuccess(
        userRole === 'PARENT'
          ? 'Player unlinked successfully'
          : 'Parent unlinked successfully'
      );
      fetchRelationships();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Unlink failed');
      } else {
        setError('Unlink failed');
      }
    }
  };

  const getDisplayUser = (rel: Relationship): User | undefined => {
    return userRole === 'PARENT' ? rel.child : rel.parent;
  };

  const title = userRole === 'PARENT' ? 'My Children (Players)' : 'My Parents';
  const addButtonText = userRole === 'PARENT' ? 'Link Player' : 'Link Parent';
  const emptyMessage =
    userRole === 'PARENT'
      ? 'No players linked yet'
      : 'No parents linked yet';

  const maxReached = userRole === 'PLAYER' && relationships.length >= 2;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          {isEditable && !maxReached && (
            <Button
              startIcon={<Add />}
              variant="outlined"
              size="small"
              onClick={handleOpenDialog}
            >
              {addButtonText}
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {userRole === 'PLAYER' && maxReached && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Maximum of 2 parents reached
          </Alert>
        )}

        {relationships.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : (
          <List>
            {relationships.map((rel) => {
              const user = getDisplayUser(rel);
              if (!user) return null;

              return (
                <ListItem key={rel.id} divider>
                  <ListItemText
                    primary={`${user.firstName} ${user.lastName}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2">
                          {user.email}
                        </Typography>
                        {user.phone && (
                          <Typography component="span" variant="body2" sx={{ ml: 2 }}>
                            📞 {user.phone}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Linked: {new Date(rel.linkedAt).toLocaleDateString()}
                        </Typography>
                      </>
                    }
                  />
                  {isEditable && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleUnlink(user.id)}
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {userRole === 'PARENT' ? 'Link a Player' : 'Link a Parent'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search by first name and/or last name
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="First Name"
                value={searchFirstName}
                onChange={(e) => setSearchFirstName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={searchLastName}
                onChange={(e) => setSearchLastName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                fullWidth
              />
            </Box>
            <Button
              variant="outlined"
              onClick={handleSearch}
              disabled={searching}
              fullWidth
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select a user to link:
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {searchResults.map((user) => (
                  <ListItemButton
                    key={user.id}
                    selected={selectedUser?.id === user.id}
                    onClick={() => setSelectedUser(user)}
                    sx={{
                      border: 1,
                      borderColor: selectedUser?.id === user.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondary={user.email}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            variant="contained"
            disabled={loading || !selectedUser}
          >
            {loading ? 'Linking...' : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
